import React, { useMemo, useEffect, useRef } from "react";
import anime from "animejs";
import { Container, Text } from "@pixi/react";
import { TextStyle, TextMetrics } from "pixi.js";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { formatToCustomLocale } from "../../../../utilities";
import { PixiSettings } from "../../../../pixiSettings";
import {
  FONT_CONFIG,
  USER_INTERFACE_BUTTON_CONFIG,
  TEXT_STYLE_CONFIG,
} from "../../../../pixiSettings";
import {
  selectDisplayedBalance,
  selectCurrency,
} from "../../../../State/selectors";

const BalanceDisplay = ({ toolbar }) => {
  const { t } = useTranslation();
  const { device, orientation, getResponsiveConfig, uiSymbolScale } =
    useDimensions();
  const displayedBalance = useSelector(selectDisplayedBalance);
  const currency = useSelector(selectCurrency);

  // Track previous balance to detect changes
  const prevBalanceRef = useRef(displayedBalance);
  const amountTextRef = useRef(null);
  const balancePressAnimRef = useRef(null);

  // Log every balance change
  useEffect(() => {
    if (prevBalanceRef.current !== displayedBalance) {
      const increased = displayedBalance > prevBalanceRef.current;
      prevBalanceRef.current = displayedBalance;

      // Trigger scale pulse only when balance increases
      if (increased && amountTextRef.current) {
        if (balancePressAnimRef.current) balancePressAnimRef.current.pause();

        const origX = amountTextRef.current.scale.x;
        const origY = amountTextRef.current.scale.y;

        // Phase 1: Quick scale up
        balancePressAnimRef.current = anime({
          targets: amountTextRef.current.scale,
          x: origX * 1.15,
          y: origY * 1.15,
          duration: 80,
          easing: "easeOutQuad",
          complete: () => {
            // Phase 2: Settle back to original
            balancePressAnimRef.current = anime({
              targets: amountTextRef.current.scale,
              x: origX,
              y: origY,
              duration: 180,
              easing: "easeOutQuad",
            });
          },
        });
      }
    }
  }, [displayedBalance]);

  // Get button dimensions using the same system as PopiClock - memoized to prevent recalculation
  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // Get unified balance configuration (font multiplier, y position) - memoized
  // X position now comes from toolbar.layout
  const balanceConfig = useMemo(() => {
    let fontMultiplier = 1.0;
    let y = 0;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.3;
        y = toolbar.height / 2;
      } else {
        fontMultiplier = 1.1;
        y = toolbar.height / 2;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.5;
        y = toolbar.height / 2;
      } else {
        fontMultiplier = 1.25;
        y = toolbar.height / 2;
      }
    } else {
      // Desktop
      fontMultiplier = 1.0;
      y = toolbar.height / 2;
    }

    return { fontMultiplier, y };
  }, [device, orientation, toolbar.height]);

  // Calculate font sizes - memoized
  const baseFontSize = 32;
  const idealFontSize = useMemo(() => {
    const responsiveFontSize = baseFontSize * balanceConfig.fontMultiplier;
    return Math.round(responsiveFontSize * uiSymbolScale);
  }, [balanceConfig.fontMultiplier, uiSymbolScale]);

  const LABEL_FONT_SIZE = useMemo(
    () => Math.round(idealFontSize * 0.8), // Slightly smaller for label
    [idealFontSize],
  );

  // Text style for balance label - memoized to prevent creating new TextStyle on every render
  const balanceLabelStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: LABEL_FONT_SIZE,
        fontWeight: "600",
        fill: "#ffbf11", // Accent yellow same as WinDisplay labels
        align: "left",
        ...TEXT_STYLE_CONFIG.OUTLINE, // Black outline with 0.5 opacity and 2px thickness
      }),
    [LABEL_FONT_SIZE],
  );

  // Format balance - memoized
  const formattedBalance = useMemo(
    () => formatToCustomLocale(displayedBalance),
    [displayedBalance],
  );

  // Prepare text content - memoized
  const balanceLabelText = useMemo(() => t("balance").toUpperCase(), [t]);
  const balanceValueText = useMemo(() => formattedBalance, [formattedBalance]);
  const balanceCurrencyText = useMemo(() => ` ${currency}`, [currency]);
  // Combined text for width calculations only
  const balanceAmountText = useMemo(
    () => `${formattedBalance} ${currency}`,
    [formattedBalance, currency],
  );

  // Calculate maximum width based on 7-digit balance to stabilize component width
  // Use ideal font size for this calculation
  const maxBalanceWidth = useMemo(() => {
    const maxBalance = 9999999; // 7 digits
    const maxBalanceText = `${formatToCustomLocale(maxBalance)} ${currency}`;
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: idealFontSize,
      fontWeight: "700",
    });
    const maxBalanceMetrics = TextMetrics.measureText(
      maxBalanceText,
      tempStyle,
    );

    return maxBalanceMetrics.width;
  }, [currency, idealFontSize]);

  /**
   * Auto-scaling font size calculation for balance amount
   * Similar to PopiBetButton: measures actual text and scales down if it exceeds max width
   * Only scales the amount, label stays fixed
   */
  const finalAmountFontSize = useMemo(() => {
    // Measure actual balance text with ideal font size
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: idealFontSize,
      fontWeight: "700",
    });
    const metrics = TextMetrics.measureText(balanceAmountText, tempStyle);

    // If text exceeds max width, scale down proportionally
    if (metrics.width > maxBalanceWidth) {
      const scale = maxBalanceWidth / metrics.width;
      return Math.round(idealFontSize * scale);
    }

    // Return ideal font size if it fits
    return idealFontSize;
  }, [balanceAmountText, idealFontSize, maxBalanceWidth]);

  const balanceAmountStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: finalAmountFontSize,
        fontWeight: "700", // Bigger font weight for value
        fill: 0xffffff, // White for numbers and currency
        align: "left",
        ...TEXT_STYLE_CONFIG.OUTLINE, // Black outline with 0.5 opacity and 2px thickness
      }),
    [finalAmountFontSize],
  );

  // Currency style — 0.7x of the amount font size
  const balanceCurrencyStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: Math.floor(finalAmountFontSize * 0.7),
        fontWeight: "700",
        fill: 0xffffff,
        align: "left",
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [finalAmountFontSize],
  );

  // Measure text dimensions for positioning - KEEPS CENTERED LABEL FEATURE - memoized
  const textMetrics = useMemo(() => {
    const labelMetrics = TextMetrics.measureText(
      balanceLabelText,
      balanceLabelStyle,
    );
    const valueMetrics = TextMetrics.measureText(
      balanceValueText,
      balanceAmountStyle,
    );
    const currencyMetrics = TextMetrics.measureText(
      balanceCurrencyText,
      balanceCurrencyStyle,
    );
    const amountLineWidth = valueMetrics.width + currencyMetrics.width;

    const totalHeight = labelMetrics.height + valueMetrics.height;
    const maxWidth = Math.max(labelMetrics.width, maxBalanceWidth);
    const labelY = Math.round(-totalHeight / 2 + labelMetrics.height / 2);
    const amountY = Math.round(totalHeight / 2 - valueMetrics.height / 2);
    const textX = Math.round(maxWidth / 2);

    return {
      labelY,
      amountY,
      textX,
      valueMetrics,
      currencyMetrics,
      amountLineWidth,
      // Bottom-align: shift smaller currency down by half the height difference
      currencyYOffset: Math.round(
        (valueMetrics.height - currencyMetrics.height) * 0.3,
      ),
    };
  }, [
    balanceLabelText,
    balanceValueText,
    balanceCurrencyText,
    balanceLabelStyle,
    balanceAmountStyle,
    balanceCurrencyStyle,
    maxBalanceWidth,
  ]);

  // Use layout position from toolbar hook if available - memoized
  const positionX = useMemo(
    () => toolbar.layout?.balance?.x ?? buttonConfig.width * 0.5,
    [toolbar.layout?.balance?.x, buttonConfig.width],
  );

  return (
    <Container
      name="secondary_balance_display"
      x={positionX}
      y={balanceConfig.y}
      zIndex={5}
      accessible={true}
      accessibleTitle={`${t("balance", { ns: "common" })}: ${formattedBalance} ${currency}`}
    >
      <Text
        key={`balance-label-column`}
        text={balanceLabelText}
        style={balanceLabelStyle}
        anchor={{ x: 0.5, y: 0.5 }}
        x={textMetrics.textX}
        y={textMetrics.labelY}
        resolution={2}
      />
      <Container ref={amountTextRef} x={textMetrics.textX} y={textMetrics.amountY} pivot={{ x: textMetrics.textX, y: 0 }}>
        <Text
          key={`balance-value`}
          text={balanceValueText}
          style={balanceAmountStyle}
          anchor={{ x: 0.5, y: 0.5 }}
          x={Math.round(
            textMetrics.textX -
              textMetrics.amountLineWidth / 2 +
              textMetrics.valueMetrics.width / 2,
          )}
          resolution={2}
        />
        <Text
          key={`balance-currency`}
          text={balanceCurrencyText}
          style={balanceCurrencyStyle}
          anchor={{ x: 0.5, y: 0.5 }}
          x={Math.round(
            textMetrics.textX +
              textMetrics.amountLineWidth / 2 -
              textMetrics.currencyMetrics.width / 2,
          )}
          y={textMetrics.currencyYOffset}
          resolution={2}
        />
      </Container>
    </Container>
  );
};

export default BalanceDisplay;
